import {Component, computed, signal, WritableSignal, inject, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog"
import {MatSelectModule} from "@angular/material/select"
import {LinkedDimensionInputComponent} from "@app/common/components/inputs/linked-dimension-input/linked-dimension-input.component"
import {NumericFieldComponent} from "@app/common/components/inputs/numeric-field/numeric-field.component"
import {MeasurementType, MeasurementTypes} from "@common/models/settings/settings"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-upload-texture-settings-dialog",
    templateUrl: "./adjust-size-dialog.component.html",
    styleUrls: ["./adjust-size-dialog.component.scss"],
    imports: [MatDialogModule, MatSelectModule, FormsModule, MatButtonModule, LinkedDimensionInputComponent, NumericFieldComponent],
})
export class AdjustSizeDialogComponent {
    readonly dialogRef = inject(MatDialogRef<AdjustSizeDialogComponent>)
    readonly data = inject<{
        width: number
        height: number
        resolutionAspectRatio: number
        displacement?: number
        showDisplacementSetting?: boolean
        allowUndefinedDisplacement?: boolean
        measurementType: MeasurementType
    }>(MAT_DIALOG_DATA)

    constructor() {
        if (!this.data) {
            throw new Error("Data is not set.")
        }
        this.$isImperial.set(this.data.measurementType === MeasurementTypes.Imperial)
        this.$showDisplacementSetting.set(this.data.showDisplacementSetting ?? false)
        this.allowUndefinedDisplacement = this.data.allowUndefinedDisplacement ?? false
        this.resolutionAspectRatio = this.data.resolutionAspectRatio
        this.$width = signal(this.$isImperial() ? MeasurementTypes.Imperial.convertFromMetric(this.data.width) : this.data.width)
        this.$height = signal(this.$isImperial() ? MeasurementTypes.Imperial.convertFromMetric(this.data.height) : this.data.height)
        this.$displacement = signal(
            this.data.displacement === undefined
                ? undefined
                : this.$isImperial()
                  ? MeasurementTypes.Imperial.convertFromMetric(this.data.displacement)
                  : this.data.displacement,
        )
    }

    cancelAction(): void {
        this.dialogRef.close(undefined)
    }

    confirmAction(): void {
        let width = this.$width()
        let height = this.$height()
        let displacement = this.$displacement()
        if (!width || !height) {
            throw new Error("Width and height must be set.")
        }
        if (this.$isImperial()) {
            width = MeasurementTypes.Imperial.convertToMetric(width)
            height = MeasurementTypes.Imperial.convertToMetric(height)
            if (displacement) {
                displacement = MeasurementTypes.Imperial.convertToMetric(displacement)
            }
        }
        this.dialogRef.close({width, height, displacement})
    }

    readonly $isImperial = signal<boolean | undefined>(undefined)
    readonly $showDisplacementSetting = signal<boolean | undefined>(undefined)
    protected allowUndefinedDisplacement: boolean
    protected resolutionAspectRatio: number
    protected $width: WritableSignal<number | undefined>
    protected $height: WritableSignal<number | undefined>
    protected $displacement: WritableSignal<number | undefined>
    protected readonly $isValidData = computed(() => {
        const width = this.$width()
        const height = this.$height()
        const displacement = this.$displacement()
        return (
            (!this.$showDisplacementSetting() || this.allowUndefinedDisplacement || (displacement !== undefined && displacement > 0)) &&
            width !== undefined &&
            width > 0 &&
            height !== undefined &&
            height > 0
        )
    })
}

export type AdjustSizeDialogResult = {
    width: number
    height: number
    displacement?: number
}
